import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import AppLayout, { usePageTitle } from "../components/AppLayout";
import { ErrorNote, Loading } from "../components/Feedback";
import { Badge, Btn, Card, G_DARK, globalStyles } from "../components/Shared";
import HostingModeBanner from "../components/HostingModeBanner";
import { useApi } from "../useApi";
import { leave, SLUG_RE, useAddress } from "./AddressContext";

// The pages of the project addresses (task 2026-10-05-each-project-has-its-own-address).
//
// Sign-in across hosts — a one-time code (decided 2026-10-05): the token lives per origin, and
// http://<label>.<base host>/ is another origin than the portal. So:
//   1. project host, signed out: ProjectHostSignIn sends the browser to <base_url>/handoff
//      ?project=<slug>&next=<path> (base_url from the backend, never from this host);
//   2. base host: HandoffStartPage (signed in there, through Keycloak as usual) asks
//      POST /projects/by-slug/<slug>/handoff for a code and follows the URL the BACKEND computed,
//      <project host>/handoff#code=...&next=...;
//   3. project host: HandoffCompletePage exchanges the code once (POST /auth/handoff) for a portal
//      token on this origin and goes on to `next`.
// ProjectHostHome is the shell the project host opens on: the project, its hosted version, and
// the way into the data interface (task O4, frontend/src/pages/operate/). While that interface
// is not on this portal (its endpoint answers 404) the shell says so instead of a dead link.

const CENTERED = { minHeight: "100vh", background: "#f5f5f5", display: "flex", alignItems: "center",
                   justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: 24 };
const PANEL = { maxWidth: 460, width: "100%", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16,
                padding: 28, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" };
const LINK = { fontSize: 14, color: G_DARK, fontWeight: 500 };

function Panel({ title, children }) {
  usePageTitle(title);
  return (
    <main style={CENTERED}>
      <style>{globalStyles}</style>
      <div style={PANEL}>
        <h1 style={{ margin: "0 0 12px", fontSize: 20, fontWeight: 700, color: "#1a1a1a" }}>{title}</h1>
        {children}
      </div>
    </main>
  );
}

function safePath(next) {
  // eslint-disable-next-line no-control-regex
  if (typeof next !== "string" || !/^\/(?![/\\])/.test(next) || /[\\\u0000-\u001f\u007f]/.test(next)) return "/";
  return next;
}

function signInUrl(address, next) {
  const q = new URLSearchParams({ project: address.slug, next: safePath(next) });
  return `${address.base_url}/handoff?${q.toString()}`;
}

// ── On the project host ──────────────────────────────────────────────────────

export function AddressesOff() {
  const address = useAddress();
  return (
    <Panel title="Project addresses are off">
      <p style={{ fontSize: 14, color: "#374151" }}>
        This portal does not open projects under their own address. An administrator can switch
        it on in Management → Project addresses.
      </p>
      <a href={`${address.base_url}/projects`} style={LINK}>Go to the portal</a>
    </Panel>
  );
}

export function ProjectHostSignIn() {
  const address = useAddress();
  const location = useLocation();
  const next = new URLSearchParams(location.search).get("next") || "/";
  const url = signInUrl(address, next);
  const sent = useRef(false);
  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    leave.to(url);
  }, [url]);
  return (
    <Panel title="Sign in">
      <p role="status" style={{ fontSize: 14, color: "#374151" }}>
        You sign in on the portal, which then brings you back to this project.
      </p>
      <a href={url} style={LINK}>Continue to the portal sign-in</a>
    </Panel>
  );
}

export function HandoffCompletePage() {
  const address = useAddress();
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [fragment] = useState(() => new URLSearchParams(window.location.hash.replace(/^#/, "")));
  const [error, setError] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (window.location.hash) {
      window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
    }
    const code = fragment.get("code");
    if (!code) { setError(new Error("This sign-in link has no code.")); return; }
    api.post("/auth/handoff", { code, slug: address.slug })
      .then(r => loginWithToken(r.access_token))
      .then(() => navigate(safePath(fragment.get("next")), { replace: true }))
      .catch(e => setError(e));
  }, [fragment, address.slug, loginWithToken, navigate]);

  if (!error) {
    return <Panel title="Signing in"><p role="status" style={{ fontSize: 14 }}>Signing you in…</p></Panel>;
  }
  return (
    <Panel title="Sign-in did not complete">
      <ErrorNote error={error} />
      <a href={signInUrl(address, fragment.get("next") || "/")} style={LINK}>Sign in again</a>
    </Panel>
  );
}

function DataEntry({ project }) {
  // The data interface of task O4. Probe it: until it is on this portal, say so.
  const tables = useApi(`/projects/${project.id}/data/tables`);
  if (tables.loading) return <Loading label="Looking for the data…" />;
  if (tables.error) {
    return (
      <p style={{ fontSize: 14, color: "#374151" }}>
        The data browser is not available on this portal yet
        {tables.error.status && tables.error.status !== 404 ? ` (${tables.error.message})` : ""}.
        The database is hosted; ask the project admin how to reach it in the meantime.
      </p>
    );
  }
  const list = Array.isArray(tables.data) ? tables.data : (tables.data && tables.data.tables) || [];
  return (
    <>
      <p style={{ fontSize: 14 }}>
        <Link to={`/projects/${project.id}/data`} style={LINK}>Open the data</Link>
      </p>
      {list.length > 0 && (
        <ul aria-label="Tables" style={{ fontSize: 14, paddingLeft: 18 }}>
          {list.map(t => <li key={t.name || t}>{t.name || t}</li>)}
        </ul>
      )}
    </>
  );
}

export function ProjectHostHome() {
  const address = useAddress();
  const r = useApi(`/projects/by-slug/${address.slug}`);
  if (r.loading) return <AppLayout title={address.host_label || "Project"}><Loading /></AppLayout>;
  if (r.error && r.error.status === 404) {
    return (
      <Panel title="No such project">
        <p style={{ fontSize: 14, color: "#374151" }}>
          There is no project at this address, or you are not a member of it.
        </p>
        <a href={`${address.base_url}/projects`} style={LINK}>Go to the portal</a>
      </Panel>
    );
  }
  if (r.error) return <AppLayout title="Project"><ErrorNote error={r.error} /></AppLayout>;
  const p = r.data;
  const portalLink = <a href={`${address.base_url}/projects/${p.id}`} style={LINK}>{p.name} on the portal</a>;
  if (!p.address) {
    return (
      <Panel title="This project is not hosted">
        <p style={{ fontSize: 14, color: "#374151" }}>
          {p.name} has no hosted database, so its address shows nothing yet. A project admin hosts
          a built database on the project's Database design page.
        </p>
        {portalLink}
      </Panel>
    );
  }
  return (
    <AppLayout title={p.name}>
      <Card style={{ marginBottom: 20 }}>
        <p style={{ margin: "0 0 10px", fontSize: 14, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <Badge>{p.hosting && p.hosting.mode === "archived" ? "Archived" : "Hosted"}</Badge>
          <span>
            version {p.hosting ? p.hosting.version : "?"}
            {p.hosting && p.hosting.database_name ? <> · <code>{p.hosting.database_name}</code></> : null}
          </span>
        </p>
        {/* maintenance or archived, with the final release's citation (components/HostingModeBanner) */}
        <HostingModeBanner hosting={p.hosting} />
        <DataEntry project={p} />
        <p style={{ margin: "14px 0 0", fontSize: 13 }}>{portalLink}</p>
      </Card>
    </AppLayout>
  );
}

// ── On the base host ─────────────────────────────────────────────────────────

export function HandoffStartPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const slug = params.get("project") || "";
  const next = safePath(params.get("next") || "/");
  const [error, setError] = useState(SLUG_RE.test(slug) ? null : new Error("This link names no project."));
  const started = useRef(false);

  useEffect(() => {
    if (started.current || !SLUG_RE.test(slug)) return;
    started.current = true;
    api.post(`/projects/by-slug/${encodeURIComponent(slug)}/handoff`, { next })
      .then(r => leave.to(r.url))
      .catch(e => setError(e));
  }, [slug, next]);

  if (!error) {
    return <Panel title="Opening the project"><p role="status" style={{ fontSize: 14 }}>Taking you to the project…</p></Panel>;
  }
  return (
    <Panel title="The project could not be opened">
      <ErrorNote error={error} />
      <Link to="/projects" style={LINK}>Back to the projects</Link>
      {" "}
      {error.status === 409 ? null : <Btn variant="secondary" onClick={() => window.location.reload()}>Try again</Btn>}
    </Panel>
  );
}
